// Salidas de productos a otras taquerías (pestaña del cuadre). Separada de Gastos:
// NO es salida de efectivo ni inventario. Cada salida genera una deuda de la taquería destino con esta.
import React, { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { listTaquerias, listSalidasTurno, crearSalidaProductos, editarSalidaProductos, anularSalidaProductos, subirComprobante, fotoSalidaProductos } from '../../services/data';
import { AdjuntarComprobante } from '../finanzas/Comprobante';
import { toMoney } from '../../utils/numbers';
import { formatDateTime } from '../../utils/dates';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import { leerBorradorLocal, guardarBorradorLocal, quitarBorradorLocal } from '../../utils/borradorLocal';
import MoneyInput from '../common/MoneyInput';

let seq = 0; const uid = () => `s${Date.now()}${seq++}`;
// Se registra con una descripción y un costo OPCIONAL. Vacío = "Valor pendiente" (no Q0.00); lo completa Administración.
// `items` solo queda en las históricas con detalle de productos, que se conservan tal cual.
const nuevaSalida = (entregadoPor = '') => ({ key: uid(), destinoId: '', entregadoPor, recibidoPor: '', reciboArchivo: null, reciboUrl: '', observacion: '', descripcion: '', monto: '', items: [], editando: true });
const desdeServidor = (s) => {
  const [i] = s.items;
  const simple = s.items.length === 1 && Number(i.cantidad) === 1 && !i.detalleCantidad;
  return { ...s, key: s.id, editando: false, descripcion: simple ? i.producto : '', monto: simple && i.total != null ? String(i.total) : '',
    items: simple ? [] : s.items.map((x) => ({ key: uid(), producto: x.producto, productoId: x.productoId || null, cantidad: String(x.cantidad),
      detalleCantidad: x.detalleCantidad || '', total: x.total == null ? '' : String(x.total) })) };
};
const datosSalida = (s) => (s.items.length
  ? { items: s.items.map((i) => ({ producto: i.producto, productoId: i.productoId || null, cantidad: i.cantidad, detalleCantidad: i.detalleCantidad, total: i.total })) }
  : { descripcion: s.descripcion, total: s.monto }); // vacío viaja vacío: el servidor lo guarda como valor pendiente
const fechaHora = (iso) => iso ? formatDateTime(iso) : '';

/** aperturaId: turno del cuadre; origenId: sucursal que entrega; editable: turno abierto o en corrección. */
export default function SalidasProductos({ aperturaId, origenId, editable, onCount, onSinGuardar, onSinFoto, children }) {
  const [taquerias, setTaquerias] = useState([]);
  const [salidas, setSalidas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busy, setBusy] = useState(false);
  const { me } = useSucursalActiva(); // "Entregó" se sugiere con el usuario actual, pero se puede cambiar

  const cargar = useCallback(async () => {
    if (!aperturaId) { setCargando(false); return; }
    try {
      const [t, lista] = await Promise.all([listTaquerias(), listSalidasTurno(aperturaId)]);
      setTaquerias(t);
      // Las salidas a medio llenar vuelven de la copia local si Android cerró el navegador antes de guardarlas.
      const sinGuardar = editable ? leerBorradorLocal(`salidas:${aperturaId}`) || [] : [];
      setSalidas([...lista.map(desdeServidor), ...sinGuardar.map((s) => ({ ...s, reciboArchivo: null }))]);
    } catch (e) { Swal.fire('Error', e.message || 'No se pudieron cargar las salidas de productos.', 'error'); }
    finally { setCargando(false); }
  }, [aperturaId, editable]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    if (cargando || !aperturaId || !editable) return;
    const nuevas = salidas.filter((s) => !s.id).map(({ reciboArchivo, ...s }) => s); // eslint-disable-line no-unused-vars
    if (nuevas.length) guardarBorradorLocal(`salidas:${aperturaId}`, nuevas); else quitarBorradorLocal(`salidas:${aperturaId}`);
  }, [salidas, cargando, aperturaId, editable]);
  useEffect(() => { onCount?.(salidas.filter((s) => s.id && s.estado !== 'Anulada').length); }, [salidas, onCount]);
  useEffect(() => { onSinGuardar?.(salidas.filter((s) => !s.id).length); }, [salidas, onSinGuardar]);
  useEffect(() => { onSinFoto?.(salidas.filter((s) => s.id && s.estado !== 'Anulada')
    .map((s, i) => ({ id: `ev-salida-${s.key}`, etiqueta: `Salida ${i + 1}`, falta: !s.reciboUrl })).filter((s) => s.falta)); }, [salidas, onSinFoto]);

  const destinos = taquerias.filter((t) => t.id !== origenId);
  const nombre = (id) => taquerias.find((t) => t.id === id)?.nombre || '';
  const set = (key, patch) => setSalidas((ss) => ss.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  // Completa o vuelve a tomar la foto de una salida guardada, sin rehacerla.
  const fotoGuardada = async (s, f) => {
    if (!f) return;
    setBusy(true);
    try {
      const url = await subirComprobante(f, (f.name || 'recibo-firmado').replace(/[^\w.-]+/g, '_'), f.type);
      const r = await fotoSalidaProductos(s.id, url);
      setSalidas((ss) => ss.map((x) => (x.key === s.key ? desdeServidor(r) : x)));
    } catch (e) { Swal.fire('No se pudo guardar la foto', e.message || 'Intenta de nuevo.', 'error'); }
    finally { setBusy(false); }
  };

  const guardar = async (s) => {
    if (!s.destinoId) return Swal.fire('Revisa la salida', 'Selecciona la taquería destino.', 'warning');
    if (!s.entregadoPor?.trim()) return Swal.fire('Revisa la salida', 'Indica quién entregó los productos.', 'warning');
    if (!s.recibidoPor?.trim()) return Swal.fire('Revisa la salida', 'Indica quién recibió los productos en la taquería destino.', 'warning');
    if (!s.items.length && !s.descripcion.trim()) return Swal.fire('Revisa la salida', 'Indica la descripción. El costo puede quedar pendiente.', 'warning');
    setBusy(true);
    try {
      // Mismo almacenamiento que los demás comprobantes: se sube al guardar la salida.
      const reciboUrl = s.reciboArchivo
        ? await subirComprobante(s.reciboArchivo, (s.reciboArchivo.name || 'recibo-firmado').replace(/[^\w.-]+/g, '_'), s.reciboArchivo.type)
        : s.reciboUrl;
      const payload = { aperturaId, destinoId: s.destinoId, entregadoPor: s.entregadoPor, recibidoPor: s.recibidoPor, reciboUrl, observacion: s.observacion, ...datosSalida(s) };
      const r = s.id ? await editarSalidaProductos(s.id, payload) : await crearSalidaProductos(payload);
      setSalidas((ss) => ss.map((x) => (x.key === s.key ? desdeServidor(r) : x)));
      Swal.fire({ icon: 'success', title: s.id ? 'Salida actualizada' : 'Salida registrada', timer: 2400, showConfirmButton: false,
        text: r.valorPendiente ? `${r.productosPendientes} producto(s) con valor pendiente: Administración completará el valor.` : `${r.destino} debe ${toMoney(r.total)} a esta taquería.` });
    } catch (e) { Swal.fire('No se pudo guardar', e.message || 'Intenta de nuevo.', 'error'); }
    finally { setBusy(false); }
  };

  const anular = async (s) => {
    const { value: motivo } = await Swal.fire({ title: 'Anular salida de productos', input: 'textarea', inputLabel: 'Motivo de la anulación',
      text: `La deuda de ${s.destino} por ${toMoney(s.total)} quedará anulada.`, showCancelButton: true, confirmButtonText: 'Anular salida',
      cancelButtonText: 'Cancelar', inputValidator: (v) => (!v?.trim() ? 'Indica el motivo' : undefined) });
    if (!motivo) return;
    setBusy(true);
    try {
      const r = await anularSalidaProductos(s.id, motivo.trim());
      setSalidas((ss) => ss.map((x) => (x.key === s.key ? desdeServidor(r) : x)));
    } catch (e) { Swal.fire('No se pudo anular', e.message || '', 'error'); }
    finally { setBusy(false); }
  };

  const cancelar = (s) => {
    if (!s.id) return setSalidas((ss) => ss.filter((x) => x.key !== s.key));
    return cargar();
  };

  if (cargando) return <p className="ct-card ct-muted">Cargando salidas de productos…</p>;
  const vigentes = salidas.filter((s) => s.id && s.estado !== 'Anulada');

  return (
    <section role="tabpanel" className="ct-lista">
      {children}
      <p className="ct-nota ct-salidas-aviso">Registra los productos que esta taquería entrega a otra. No sale efectivo de la caja ni es un gasto:
        genera una cuenta por cobrar a la taquería destino.</p>
      {!salidas.length && !editable && <p className="ct-card ct-muted">Sin salidas de productos en este turno.</p>}
      {salidas.map((s, si) => {
        // Estado por salida (mismo patrón que Compras y Gastos): una salida guardada (con id) está COLAPSADA;
        // se EXPANDE si es nueva (sin id), si el usuario tocó Editar (`editando`) o "Ver" (`verDetalle`, solo lectura).
        const abierta = editable && s.editando;
        const anulada = s.estado === 'Anulada';
        const modificable = editable && !anulada && s.pagado <= 0; // con pagos o anulada no se edita ni anula (reglas existentes)
        const expandida = !s.id || s.editando || s.verDetalle;
        const etiqueta = anulada ? 'Anulada' : s.pagado > 0 ? s.estadoCuenta : s.valorPendiente ? 'Valor pendiente' : '';
        if (!expandida) return (
          <article id={`ev-salida-${s.key}`} className={`ct-card ct-compra-fila${anulada ? ' ct-salida--anulada' : ''}`} key={s.key}>
            <span className="ct-fila-titulo"><b>{s.destino || nombre(s.destinoId)} · {s.valorPendiente && !s.total ? 'Valor pendiente' : toMoney(s.total)}</b>
              {s.descripcion && <small> · {s.descripcion}</small>}{etiqueta && !(s.valorPendiente && !s.total) && <small> · {etiqueta}</small>}</span>
            <span className="ct-salida-acciones">
              {modificable ? <>
                <button type="button" className="ct-link" disabled={busy} onClick={() => set(s.key, { editando: true })}>Editar</button>
                <button type="button" className="ct-link ct-danger" disabled={busy} onClick={() => anular(s)}>Anular</button>
              </> : <button type="button" className="ct-link" onClick={() => set(s.key, { verDetalle: true })}>Ver</button>}
            </span>
            {editable && !anulada && <div className="ct-fila-foto"><AdjuntarComprobante archivo={null} url={s.reciboUrl} soloImagen requerido onChange={(f) => fotoGuardada(s, f)} /></div>}
          </article>
        );
        return (
          <article id={`ev-salida-${s.key}`} className={`ct-card${anulada ? ' ct-salida--anulada' : ''}`} key={s.key}>
            <div className="ct-card-head">
              <h3>Salida {si + 1}{s.id && <small> · {fechaHora(s.fechaAt)} · {s.usuario}</small>}
                {etiqueta && <small> · {etiqueta}{s.pagado > 0 && !anulada ? ` (${toMoney(s.pagado)} pagado)` : ''}</small>}</h3>
              {/* Quitar (nueva) o Cancelar (en edición) arriba a la derecha, como en Compras y Gastos. */}
              {abierta ? <button className={s.id ? 'ct-link' : 'ct-link ct-danger'} disabled={busy} onClick={() => cancelar(s)}>{s.id ? 'Cancelar' : 'Quitar'}</button>
                : s.verDetalle && <button className="ct-link" onClick={() => set(s.key, { verDetalle: false })}>Ocultar</button>}
            </div>
            <div className="ct-form">
              <label className="ct-span2">Taquería destino<select value={s.destinoId} disabled={!abierta} onChange={(e) => set(s.key, { destinoId: e.target.value })}>
                <option value="">Selecciona</option>
                {destinos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                {s.destinoId && !destinos.some((t) => t.id === s.destinoId) && <option value={s.destinoId}>{s.destino || nombre(s.destinoId)}</option>}
              </select></label>
              {/* Entregó | Recibido por en una fila, como los pares de Compras. */}
              <div className="ct-span2 ct-par">
              <label>Entregó<input value={s.entregadoPor} disabled={!abierta} maxLength={120} placeholder="Quién entrega en esta taquería"
                onChange={(e) => set(s.key, { entregadoPor: e.target.value })} /></label>
              <label>Recibido por<input value={s.recibidoPor} disabled={!abierta} maxLength={120} placeholder="Quién recibe en la taquería destino"
                onChange={(e) => set(s.key, { recibidoPor: e.target.value })} /></label>
              </div>
              {s.items.length > 0 ? <p className="ct-span2 ct-muted">Productos: {s.items.map((i) => i.producto).join(', ')}</p> : <>
                <label className="ct-span2">Descripción<input value={s.descripcion} disabled={!abierta} maxLength={300} placeholder="Ej.: Tortillas y salsas" onChange={(e) => set(s.key, { descripcion: e.target.value })} /></label>
                <label className="ct-span2">Costo (opcional)<span className="ct-money-input"><b>Q</b><MoneyInput prefijo={false} value={s.monto} disabled={!abierta} aria-label="Costo (opcional; vacío = valor pendiente)" onChange={(v) => set(s.key, { monto: v })} /></span>
                  {String(s.monto).trim() === '' && <small className="ct-valor-pendiente">Valor pendiente</small>}</label>
              </>}
              <div className="ct-span2 ct-adjunto-ancho"><span className="ct-campo-titulo">Foto de la entrega</span>
                {!abierta && !s.reciboUrl ? <small className="ct-muted">Pendiente de foto</small>
                  : <AdjuntarComprobante archivo={s.reciboArchivo} url={s.reciboUrl} disabled={!abierta} soloImagen requerido={abierta} ayuda="Recibo de entrega firmado."
                    onChange={(f) => set(s.key, f ? { reciboArchivo: f } : { reciboArchivo: null, reciboUrl: '' })} />}</div>
            </div>
            {s.id && anulada && s.anuladoMotivo && <p className="ct-muted">Anulada por {s.anuladoPor}: {s.anuladoMotivo}</p>}
            {abierta && <button type="button" className="ct-btn ct-btn--primary" disabled={busy} onClick={() => guardar(s)}>{s.id ? 'Guardar cambios' : 'Guardar salida'}</button>}
          </article>
        );
      })}
      {vigentes.length > 0 && <div className="ct-total">Total salidas de productos <strong>{toMoney(vigentes.reduce((t, s) => t + s.total, 0))}</strong></div>}
      {editable && <button className="ct-btn ct-btn--primary" onClick={() => setSalidas((ss) => [...ss, nuevaSalida(me?.username || '')])}>+ Agregar salida de productos</button>}
    </section>
  );
}
